import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import BlockPalette from '../components/BlockPalette';
import EditableBlock from '../components/EditableBlock';
import TemplatePreview from '../components/TemplatePreview';
import { actionIcons } from '../utils/iconMap';
import apiClient from '../utils/axios';

export default function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState({ blockId: null, operation: null });
  const [loading, setLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (id) {
      loadTemplate();
    } else {
      // New template - start with empty structure
      setBlocks([]);
      setLoading(false);
    }
  }, [id]);

  const loadTemplate = async () => {
    try {
      const response = await apiClient.get(`/templates/${id}`);
      const templateData = response.data.template;

      setTemplate(templateData);

      // Load block structure if available
      let loadedBlocks = [];
      
      if (templateData.block_structure) {
        // block_structure might be a JSON string or already an object
        let blockStructure = templateData.block_structure;
        if (typeof blockStructure === 'string') {
          try {
            blockStructure = JSON.parse(blockStructure);
          } catch (e) {
            console.error('Error parsing block_structure:', e);
          }
        }
        
        if (blockStructure && blockStructure.blocks && Array.isArray(blockStructure.blocks)) {
          loadedBlocks = blockStructure.blocks;
        }
      }
      
      // If no block structure, convert HTML to blocks
      if (loadedBlocks.length === 0 && templateData.html_content) {
        loadedBlocks = htmlToBlocks(templateData.html_content);
      }

      setBlocks(loadedBlocks);
      setLoading(false);
    } catch (error) {
      console.error('Error loading template:', error);
      alert(`Error loading template: ${error.response?.data?.message || error.message}`);
      setLoading(false);
    }
  };

  const htmlToBlocks = (html) => {
    const blocks = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Remove script and style tags
    const scripts = doc.querySelectorAll('script, style');
    scripts.forEach(el => el.remove());
    
    // Get the main content area (usually in a table or div)
    let contentArea = doc.body;
    
    // Try to find the main content container
    const mainContent = doc.querySelector('table[role="presentation"] td, .email-content, [style*="padding"]');
    if (mainContent) {
      contentArea = mainContent;
    }
    
    // Process all direct children
    const processElement = (element, parentBlockId = null) => {
      if (!element || element.nodeType !== 1) return; // Not an element node
      
      const tagName = element.tagName.toLowerCase();
      const textContent = element.textContent?.trim() || '';
      
      // Skip empty elements
      if (!textContent && !element.querySelector('img')) return;
      
      let block = null;
      
      // Headers
      if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        const level = parseInt(tagName.charAt(1)) || 1;
        block = {
          id: `block-${Date.now()}-${Math.random()}`,
          type: 'header',
          content: textContent,
          props: { level, align: getTextAlign(element) },
        };
      }
      // Paragraphs
      else if (tagName === 'p') {
        block = {
          id: `block-${Date.now()}-${Math.random()}`,
          type: 'text',
          content: textContent,
          props: { align: getTextAlign(element) },
        };
      }
      // Links/Buttons
      else if (tagName === 'a') {
        const href = element.getAttribute('href') || '#';
        const isButton = element.style.backgroundColor || 
                       element.classList.contains('button') ||
                       element.style.padding?.includes('px');
        
        if (isButton) {
          block = {
            id: `block-${Date.now()}-${Math.random()}`,
            type: 'button',
            content: textContent,
            props: { url: href, align: getTextAlign(element.parentElement) || 'center' },
          };
        } else {
          // Regular link - convert to text with link
          block = {
            id: `block-${Date.now()}-${Math.random()}`,
            type: 'text',
            content: textContent,
            props: {},
          };
        }
      }
      // Images
      else if (tagName === 'img') {
        const src = element.getAttribute('src') || '';
        block = {
          id: `block-${Date.now()}-${Math.random()}`,
          type: 'image',
          content: element.getAttribute('alt') || '',
          props: { src, align: getTextAlign(element.parentElement) || 'center' },
        };
      }
      // Horizontal rules
      else if (tagName === 'hr') {
        block = {
          id: `block-${Date.now()}-${Math.random()}`,
          type: 'divider',
          content: '',
          props: {},
        };
      }
      // Divs with content
      else if (tagName === 'div' && textContent) {
        // Check if it's a spacer
        const height = element.style.height;
        if (height && !textContent) {
          const heightValue = parseInt(height) || 20;
          block = {
            id: `block-${Date.now()}-${Math.random()}`,
            type: 'spacer',
            content: '',
            props: { height: heightValue },
          };
        } else if (textContent) {
          block = {
            id: `block-${Date.now()}-${Math.random()}`,
            type: 'text',
            content: textContent,
            props: {},
          };
        }
      }
      
      if (block) {
        blocks.push(block);
      }
      
      // Process children recursively
      Array.from(element.children).forEach(child => {
        processElement(child, block?.id);
      });
    };
    
    // Process all top-level elements
    Array.from(contentArea.children).forEach(child => {
      processElement(child);
    });
    
    // If no blocks found, create a text block with all content
    if (blocks.length === 0) {
      const allText = contentArea.textContent?.trim() || '';
      if (allText) {
        blocks.push({
          id: `block-${Date.now()}`,
          type: 'text',
          content: allText.substring(0, 500), // Limit to first 500 chars
          props: {},
        });
      }
    }
    
    return blocks;
  };
  
  const getTextAlign = (element) => {
    if (!element) return 'left';
    const align = element.style.textAlign || 
                 element.getAttribute('align') ||
                 window.getComputedStyle(element).textAlign;
    return align || 'left';
  };
  
  const extractTextFromHTML = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    // Handle dropping from palette
    if (active.data.current?.type === 'palette-item') {
      const blockType = active.data.current.blockType;
      const newBlock = {
        id: `block-${Date.now()}-${Math.random()}`,
        type: blockType,
        content: active.data.current.defaultContent || '',
        props: getDefaultProps(blockType),
      };
      setBlocks([...blocks, newBlock]);
    }
    // Handle reordering blocks
    else if (active.id !== over.id) {
      const oldIndex = blocks.findIndex((block) => block.id === active.id);
      const newIndex = blocks.findIndex((block) => block.id === over.id);
      setBlocks(arrayMove(blocks, oldIndex, newIndex));
    }

    setActiveId(null);
  };

  const getDefaultProps = (type) => {
    switch (type) {
      case 'header':
        return { level: 1, align: 'left', fontSize: '32px' };
      case 'button':
        return { url: '#', color: 'blue', align: 'center' };
      case 'image':
        return { src: '', align: 'center', width: '100%' };
      case 'spacer':
        return { height: 20 };
      default:
        return {};
    }
  };

  const handleUpdateBlock = (blockId, updatedBlock) => {
    setBlocks(blocks.map((block) => (block.id === blockId ? updatedBlock : block)));
  };

  const handleDeleteBlock = (blockId) => {
    setBlocks(blocks.filter((block) => block.id !== blockId));
  };

  const handleAIOperation = async (blockId, operation, selectedText, tone = null) => {
    if (!id) {
      alert('Please save the template first before using AI features');
      return;
    }

    setAiLoading({ blockId, operation });

    try {
      const endpoint = tone 
        ? `/templates/${id}/ai-tone`
        : `/templates/${id}/ai-${operation}`;

      const response = await apiClient.post(endpoint, {
        selectedText,
        tone,
        context: 'email template for VoIP sales',
      });

      const newText = response.data.result;

      // Replace selected text in block
      const block = blocks.find((b) => b.id === blockId);
      if (block) {
        const updatedContent = block.content.replace(selectedText, newText);
        handleUpdateBlock(blockId, { ...block, content: updatedContent });
      }
    } catch (error) {
      console.error('AI operation error:', error);
      alert('AI operation failed. Please try again.');
    } finally {
      setAiLoading({ blockId: null, operation: null });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const blockStructure = {
        blocks,
        styles: {
          backgroundColor: '#f3f4f6',
          fontFamily: 'Arial, sans-serif',
        },
      };

      // Convert blocks to HTML
      const htmlResponse = await apiClient.post('/templates/convert-blocks', {
        block_structure: blockStructure,
      });

      const updates = {
        html_content: htmlResponse.data.html_content,
        plain_text_content: htmlResponse.data.plain_text_content,
        block_structure: blockStructure,
      };

      if (id) {
        // Update existing template - combine both updates in one call
        await apiClient.put(`/templates/${id}`, updates);
      } else {
        // Create new template
        const response = await apiClient.post('/templates', {
          name: `Template ${new Date().toLocaleDateString()}`,
          ...updates,
        });
        navigate(`/templates/editor/${response.data.template.id}`);
        return;
      }

      alert('Template saved successfully!');
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const SaveIcon = actionIcons.save;
  const LoadingIcon = actionIcons.loading;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingIcon className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {template?.name || 'New Template'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">Drag blocks to build your email template</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/templates')}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <LoadingIcon size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <SaveIcon size={16} />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Block Palette */}
        <BlockPalette />

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-8">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-lg shadow-sm p-6 min-h-[600px]">
                {blocks.length === 0 ? (
                  <div className="text-center py-20 text-gray-400">
                    <p className="text-lg mb-2">Start building your template</p>
                    <p className="text-sm">Drag blocks from the left sidebar</p>
                  </div>
                ) : (
                  <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                    {blocks.map((block) => (
                      <EditableBlock
                        key={block.id}
                        block={block}
                        onUpdate={handleUpdateBlock}
                        onDelete={handleDeleteBlock}
                        onAIOperation={handleAIOperation}
                        isAILoading={aiLoading.blockId === block.id}
                      />
                    ))}
                  </SortableContext>
                )}
              </div>
            </div>

            <DragOverlay>
              {activeId ? (
                <div className="bg-white border border-blue-300 rounded-lg p-4 shadow-lg opacity-90">
                  Dragging...
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        {/* Preview Panel */}
        <TemplatePreview blocks={blocks} />
      </div>
    </div>
  );
}

